import { Reducer } from 'redux';
import { ACTION } from './action-types';
import * as actions from './action-types';
import { reducer as simpleFormReducer, INITIAL_SIMPLEFORM_STATE } from 'app/infrastructure/simple-form/reducers';
import { STATE } from 'app/constants';
import { ImportState } from './types-store';

export const INITIAL_STATE: ImportState = {
  importList: undefined,
  fetchingImportList: undefined,
  importCaseToEdit: undefined,
  fetchingAddImportCase: undefined,
  editedImportId: undefined,
  updatedImportCases: [],
  texts: {},
  validation: {},
  ...INITIAL_SIMPLEFORM_STATE
};

const reducers: { [indexer: string]: Reducer<ImportState> } = {

  /**
   * Obtaining imports list
   */
  [ACTION.REQUEST_IMPORT_LIST]: (state: ImportState, action: actions.RequestImportList): ImportState => {
    return {
      ...state,
      fetchingImportList: true
    };
  },

  [ACTION.RECEIVE_IMPORT_LIST]: (state: ImportState, action: actions.ReceiveImportList): ImportState => {

    return {
      ...state,
      importList: action.importList,
      fetchingImportList: false
    };
  },

  [ACTION.FAIL_IMPORT_LIST]: (state: ImportState, action: actions.FailImportList): ImportState => {
    return {
      ...state,
      fetchingImportList: false
    };
  },

  /**
   * Loading more items in import list
   */
  [ACTION.REQUEST_MORE_IMPORT_LIST_ITEMS]: (state: ImportState, action: actions.RequestMoreImportListItems): ImportState => {
    return {
      ...state,
      fetchingImportList: true,
      fetchingMoreImportListItems: true
    };
  },

  [ACTION.RECEIVE_MORE_IMPORT_LIST_ITEMS]: (state: ImportState, action: actions.ReceiveMoreImportListItems): ImportState => {
    return {
      ...state,
      fetchingImportList: false,
      fetchingMoreImportListItems: false,
      importList: [...(state.importList ?? []), ...action.importList],
      reachedEndOfList: action.reachedEndOfList
    };
  },

  [ACTION.FAIL_MORE_IMPORT_LIST_ITEMS]: (state: ImportState, action: actions.FailMoreImportListItems): ImportState => {
    return {
      ...state,
      fetchingImportList: false,
      fetchingMoreImportListItems: false
    };
  },

  /**
   * Adding new import
   */
  [ACTION.REQUEST_ADD_IMPORT]: (state: ImportState, action: actions.RequestAddImport): ImportState => {

    return {
      ...state,
      fetchingAddImportCase: true
    };
  },

  [ACTION.RECEIVE_ADD_IMPORT]: (state: ImportState, action: actions.ReceiveAddImport): ImportState => {
    return {
      ...state,
      fetchingAddImportCase: false,
      reachedEndOfList: false
    };
  },

  [ACTION.FAIL_ADD_IMPORT]: (state: ImportState, action: actions.FailAddImport): ImportState => {

    return {
      ...state,
      fetchingAddImportCase: false
    };
  },

  [ACTION.CLEAR_IMPORT_CASE]: (state: ImportState, action: actions.ClearImportCase): ImportState => {

    return {
      ...state,
      fetchingAddImportCase: false
    };
  },

  /**
   * Editing import
   */

  // Fetch import data
  [ACTION.REQUEST_LOAD_EDIT_IMPORT]: (state: ImportState, action: actions.RequestLoadEditImport): ImportState => {
    return {
      ...state,
      editedImportId: action.importCaseId
    };
  },

  [ACTION.RECEIVE_LOAD_EDIT_IMPORT]: (state: ImportState, action: actions.ReceiveLoadEditImport): ImportState => {

    return {
      ...state,
      importCaseToEdit: action.importCase
    };
  },

  [ACTION.FAIL_LOAD_EDIT_IMPORT]: (state: ImportState, action: actions.FailLoadEditImport): ImportState => {
    return {
      ...state,
      importCaseToEdit: undefined,
      editedImportId: undefined
    };
  },

  // Import details
  [ACTION.REQUEST_LOAD_IMPORT_DETAILS]: (state: ImportState, action: actions.RequestLoadImportDetails): ImportState => {
    return {
      ...state,
      importCaseDetails: {
        fetching: true,
        id: action.importCaseId,
        error: undefined,
        importCase: undefined
      }
    };
  },

  [ACTION.RECEIVE_LOAD_IMPORT_DETAILS]: (state: ImportState, action: actions.ReceiveLoadImportDetails): ImportState => {
    if (action.importCase?.id !== state?.importCaseDetails?.id) {
      return state;
    }

    return {
      ...state,
      importCaseDetails: {
        fetching: false,
        id: action.importCase.id,
        error: undefined,
        importCase: action.importCase
      }
    };
  },

  [ACTION.FAIL_LOAD_IMPORT_DETAILS]: (state: ImportState, action: actions.FailLoadImportDetails): ImportState => {
    if (action.importCaseId !== state.importCaseDetails?.id) {
      return state;
    }

    return {
      ...state,
      importCaseDetails: {
        fetching: false,
        error: true,
        id: action.importCaseId,
        importCase: undefined
      }
    };
  },

  [ACTION.CLEAR_IMPORT_DETAILS]: (state: ImportState, action: actions.ClearImportDetails): ImportState => {
    return {
      ...state,
      importCaseDetails: undefined
    };
  },

  // Update import data
  [ACTION.REQUEST_UPDATE_IMPORT]: (state: ImportState, action: actions.RequestUpdateImport): ImportState => {

    return {
      ...state,
      updatedImportCases: state.updatedImportCases.some(id => id === action.importCaseId)
        ? state.updatedImportCases
        : [
          ...state.updatedImportCases,
          action.importCaseId
        ],
      importCaseToEdit: undefined,
      editedImportId: undefined
    };
  },

  [ACTION.RECEIVE_UPDATE_IMPORT]: (state: ImportState, action: actions.ReceiveUpdateImport): ImportState => {

    return {
      ...state,
      importList: state.importList
        .map(importCase => importCase.id === action.importCaseId
          ? {
            ...importCase,
            comments: action.serializeData.comments
          }
          : importCase),
      updatedImportCases: state.updatedImportCases.filter(id => id !== action.importCaseId)
    };
  },

  [ACTION.FAIL_UPDATE_IMPORT]: (state: ImportState, action: actions.FailUpdateImport): ImportState => {

    return {
      ...state,
      updatedImportCases: state.updatedImportCases.filter(id => id !== action.importCaseId)
    };
  },

  [ACTION.CLEAR_EDIT_IMPORT]: (state: ImportState, action: actions.ClearEditImport): ImportState => {

    return {
      ...state,
      importCaseToEdit: undefined,
      editedImportId: undefined
    };
  },

  /**
   * Retry import
   */
  [ACTION.REQUEST_RETRY_IMPORT]: (state: ImportState, action: actions.RequestRetryImport): ImportState => {

    return {
      ...state,
      updatedImportCases: state.updatedImportCases.some(id => id === action.importCaseId)
        ? state.updatedImportCases
        : [
          ...state.updatedImportCases,
          action.importCaseId
        ]
    };
  },

  [ACTION.RECEIVE_RETRY_IMPORT]: (state: ImportState, action: actions.ReceiveRetryImport): ImportState => {

    return {
      ...state,
      updatedImportCases: state.updatedImportCases.filter(id => id !== action.importCaseId)
    };
  },

  [ACTION.FAIL_RETRY_IMPORT]: (state: ImportState, action: actions.FailRetryImport): ImportState => {

    return {
      ...state,
      updatedImportCases: state.updatedImportCases.filter(id => id !== action.importCaseId)
    };
  },

  /**
   * Delete import
   */
  [ACTION.REQUEST_DELETE_IMPORT]: (state: ImportState, action: actions.RequestDeleteImport): ImportState => {

    return {
      ...state,
      importList: [...state.importList.filter(importCase => importCase.id !== action.importCaseId)],
      updatedImportCases: state.updatedImportCases.some(id => id === action.importCaseId)
        ? state.updatedImportCases
        : [
          ...state.updatedImportCases,
          action.importCaseId
        ]
    };
  },

  [ACTION.RECEIVE_DELETE_IMPORT]: (state: ImportState, action: actions.ReceiveDeleteImport): ImportState => {
    return {
      ...state,
      updatedImportCases: state.updatedImportCases.filter(id => id !== action.importCaseId)
    };
  },

  [ACTION.FAIL_DELETE_IMPORT]: (state: ImportState, action: actions.FailDeleteImport): ImportState => {

    return {
      ...state,
      updatedImportCases: state.updatedImportCases.filter(id => id !== action.importCaseId)
    };
  }
};

export const reducer: Reducer<ImportState> = (state: ImportState = INITIAL_STATE, action: any) => {
  const r = reducers[action.type];
  return (r && r(state, action)) || simpleFormReducer(STATE.import)(state, action);
};
